import { useMutation } from '@tanstack/react-query';
import { api, ensureCsrf } from '@/shared/api/client';
import { useAuth, type TenantSummary, type User } from './store';
import { loginPath, rememberLoginKey } from './login-link';

interface LoginPayload {
  email: string;
  password: string;
  remember?: boolean;
  /**
   * The company the operator picked on the login page's dropdown. The
   * backend validates access, stores the id on the session (so
   * ScopeToCurrentCompany reads it on the next request) and echoes it
   * back on the response so the axios client can seed the header for
   * the first authenticated call before /me/current-company has run.
   */
  company_id?: number | null;
  /**
   * The key from a per-industry sign-in link (/login/pharma). The backend
   * checks the account belongs to that company BEFORE creating a session,
   * then pins the session to it for its whole life.
   */
  login_key?: string | null;
}

interface LoginResponse {
  data: {
    user: User;
    tenants: TenantSummary[];
    token: string | null;
    /** Present when the backend has confirmed the picked company. */
    current_company_id?: number | null;
    /** Non-null when the user picked a company they don't have access to. */
    company_access_error?: string | null;
    /** Primary roles (Spatie names) — used by the SPA for the post-login redirect. */
    roles?: string[];
    /** True when the session was opened from a sign-in link and is pinned. */
    locked?: boolean;
    /** The link's key, echoed back so sign-out can return to the same link. */
    login_key?: string | null;
  };
}

export function useLogin() {
  const setSession = useAuth((s) => s.setSession);
  return useMutation({
    mutationFn: async (payload: LoginPayload) => {
      await ensureCsrf();
      const { data } = await api.post<LoginResponse>('/api/v1/auth/login', payload);
      setSession({
        user: data.data.user,
        tenants: data.data.tenants,
        currentTenantId: data.data.tenants[0]?.id ?? null,
        roles: data.data.roles ?? [],
      });
      // Remember the link this session came from (or forget it, for a plain
      // sign-in) so signing out and an expired session both come back here.
      rememberLoginKey(data.data.login_key ?? null);
      return data.data;
    },
  });
}

export function useLogout() {
  const clear = useAuth((s) => s.clear);
  return useMutation({
    mutationFn: async () => {
      await api.post('/api/v1/auth/logout');
    },
    onSettled: () => {
      clear();
      // Back to the sign-in link this person came in through, so a prospect
      // who signs out lands on their own company's page — not a bare form.
      window.location.href = loginPath();
    },
  });
}
